import { useState } from 'react';
import { Field, Modal, Spinner } from '../components';
import { useI18n } from '../i18n';
import { describe } from '../errors';

export type ContactFields = {
  name: string;
  contactName?: string | null;
  phone?: string | null;
  whatsappPhone?: string | null;
  email?: string | null;
  address?: string | null;
  notes?: string | null;
};

/**
 * Editing a supplier or a laboratory.
 *
 * One component for both because they are the same record with a different
 * name on it — the same fields, the same rules, and the same reason for
 * existing: a phone number changes, a contact leaves, someone mistyped the
 * name. Until now neither could be corrected at all, which meant the only fix
 * for a typo was a second row and a purchase history split across the two.
 *
 * Sends only what CHANGED. A patch that always carries every field would
 * overwrite a value someone else edited while this dialog was open, and would
 * make the audit entry claim ten fields were touched when one was.
 */
export function EditContact({
  title,
  initial,
  onSave,
  onClose,
  onDone,
}: {
  title: string;
  initial: ContactFields;
  onSave: (patch: Record<string, unknown>) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: initial.name ?? '',
    contactName: initial.contactName ?? '',
    phone: initial.phone ?? '',
    whatsappPhone: initial.whatsappPhone ?? '',
    email: initial.email ?? '',
    address: initial.address ?? '',
    notes: initial.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  function changedFields(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const key of Object.keys(form) as (keyof typeof form)[]) {
      const before = (initial[key as keyof ContactFields] ?? '') as string;
      const after = form[key].trim();
      if (after === before) continue;
      // An emptied field means "not recorded", which is null rather than an
      // empty string — the two read differently in every report.
      patch[key] = key === 'name' ? after : after === '' ? null : after;
    }
    return patch;
  }

  const patch = changedFields();

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || form.name.trim() === '' || Object.keys(patch).length === 0}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onSave(patch);
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <Field label={t.inventory.name} required>
          <input value={form.name} onChange={set('name')} autoFocus />
        </Field>
        <div className="grid-2">
          <Field label={t.inventory.contact} hint={t.patients.optional}>
            <input value={form.contactName} onChange={set('contactName')} />
          </Field>
          <Field label={t.patients.phone} hint={t.patients.optional}>
            <input value={form.phone} onChange={set('phone')} className="ltr-value" />
          </Field>
          <Field label={t.patients.whatsapp} hint={t.patients.optional}>
            <input value={form.whatsappPhone} onChange={set('whatsappPhone')} className="ltr-value" />
          </Field>
          <Field label={t.patients.emailAddress} hint={t.patients.optional}>
            <input value={form.email} onChange={set('email')} className="ltr-value" />
          </Field>
        </div>
        <Field label={t.inventory.address} hint={t.patients.optional}>
          <input value={form.address} onChange={set('address')} />
        </Field>
        <Field label={t.patients.notes} hint={t.patients.optional}>
          <textarea value={form.notes} onChange={set('notes')} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}
